import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { dashboardAction } from "../../_store/dashboard.slice";
import { authActions, connectionRequestActions, masterActions, messagesAction, photoAction, postalMailActions, staffRequestAction } from "../../_store";
import "../../assets/css/AdminDashboard.css";
import { getStoredValue } from "../../_helpers/storagehelper";
import CustomLoader from "../../components/common/ui/Loader";

const AdminDashboard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState("Photos");
    const [sidebarMenu, setSidebarMenu] = useState([]);
    const [chartData, setChartData] = useState([]);

    const authUser = useSelector((state) => state.auth.user);
    const facilityCode = useSelector((state) => state.auth.commonFacilityCode);
    const pageReloadCount = useSelector((state) => state.master.isPageReload);
    const dashboardData = useSelector((state) => state.dashboard.getAdminDashboardInfo);
    const [isLoading, setisLoading] = useState(false);

    // ------------------------------
    // Sidebar Config
    // ------------------------------
    const notificationConfig = [
        {
            title: "Photos",
            icon: "fa-solid fa-images fa-lg",
            buttonText: "View Photos",
            type: "Photos",
        },
        {
            title: "Postal Mails",
            icon: "fa-solid fa-envelope fa-lg",
            buttonText: "View Postal Mail",
            type: "Postal Mail",
        },
        {
            title: "Staff Requests",
            icon: "fa-solid fa-person-circle-question fa-lg",
            buttonText: "View Staff Requests",
            type: "Request Form",
        },
        {
            title: "Connection Requests",
            icon: "fa fa-hourglass-half fa-lg",
            buttonText: "View Connection Requests",
            type: "Connection Request",
        },
        {
            title: "Quarantine Messages",
            icon: "fa fa-commenting fa-lg",
            buttonText: "View Pending Quarantine",
            type: "Quarantine",
        },
    ];

    // ------------------------------
    // Fetch Dashboard Data
    // ------------------------------
    useEffect(() => {
        const managementUserID = authUser?.ManagementUserID;
        const facilityID = 0;
        setisLoading(true);
        dispatch(
            dashboardAction.GetDashboardNotificationCount({
                managementUserID,
                facilityID,
            })
        ).then((res) => {
            const alerts = res?.payload?.NotificationAlerts || [];
            const filteredAlerts = alerts.filter((item) => item.NotificationCount > 0);

            const mergedMenu = notificationConfig.map((item) => {
                const matched = filteredAlerts.find(
                    (alert) => alert.NotificationType === item.type
                );
                return {
                    ...item,
                    NotificationCount: matched?.NotificationCount || 0,
                };
            });
            setisLoading(false);
            setSidebarMenu(mergedMenu);
            if (mergedMenu.length > 0) setActiveTab(mergedMenu[0].type);
        });
    }, [facilityCode, dispatch]);

    // ------------------------------
    // Handle Active Tab Data
    // ------------------------------
    useEffect(() => {
        const facilityNotifications = dashboardData?.FacilityNotifications || [];
        if (!facilityNotifications.length) return;

        let filteredData = [];

        switch (activeTab) {
            case "Photos":
                filteredData = facilityNotifications.filter((x) => x.MessageStatus === "All Facility Photos");
                break;

            case "Postal Mail":
                filteredData = facilityNotifications.filter((x) => x.MessageStatus === "All Facility PostalMail");
                break;

            case "Staff Requests":
            case "Request Form":
                filteredData = facilityNotifications.filter((x) => x.MessageStatus === "All Facility Request Form");
                break;

            case "Connection Requests":
            case "Connection Request":
                filteredData = facilityNotifications.filter((x) => x.MessageStatus === "All Facility Connection Request");
                break;

            case "Quarantine":
                filteredData = facilityNotifications.filter((x) => x.MessageStatus === "All Facility Quarantine Message");
                break;

            default:
                filteredData = facilityNotifications.filter((x) => x.MessageStatus === "All Facility Photos");
                break;
        }
        // Remove duplicates based on FacilityCode
        const uniqueData = filteredData.filter(
            (item, index, self) =>
                index === self.findIndex((t) => t.FacilityCode === item.FacilityCode)
        );

        setChartData(uniqueData);
    }, [activeTab, dashboardData]);

    // ------------------------------
    // Facility Selection
    // ------------------------------
    const handleFacilitySelection = (facilityId) => {
        if (!facilityId) return;

        dispatch(authActions.updateCommonFacilityCode(facilityId));
        dispatch(masterActions.updateLocation(pageReloadCount + 1));

        const user = getStoredValue("user");
        const managementUserID = authUser?.ManagementUserID ?? user?.ManagementUserID ?? 0;

        dispatch(
            masterActions.GetLoadNotificationAlert({
                ManagementUserID: managementUserID,
                FacilityID: facilityId,
            })
        ).then((response) => {
            const updatedMenuList = getStoredValue("menu_list") || response?.payload;
            if (updatedMenuList) {
                dispatch(authActions.updateMenuList(updatedMenuList));
            }
        });
    };

    // ------------------------------
    // Facility Bar Click
    // ------------------------------
    const handleFacilityClick = (data) => {
        if (data?.FacilityID) {
            handleFacilitySelection(data?.FacilityID);
            handleCardNavigation(activeTab);
        }
    };

    // ------------------------------
    // Card Navigation
    // ------------------------------
    const handleCardNavigation = (type) => {
        dispatch(masterActions.UpdateisDashboardPage(true));
        dispatch(masterActions.UpdateisBacktoDashboard(true));
        dispatch(masterActions.updateismsgFlagDisabled(true));
        switch (type) {
            case "Photos":
                dispatch(photoAction?.resetPhotoState());
                navigate("/Admin/PhotoList");
                break;
            case 'Postal Mail':
                dispatch(postalMailActions.clearPostalMail());
                dispatch(postalMailActions.clearSearchPostalMail());
                navigate("/Admin/PostalMailList");
                break;
            case "Staff Requests":
            case "Request Form":
                dispatch(staffRequestAction?.resetStaffRequestSearcList());
                dispatch(staffRequestAction?.resetPublicDefenderSearchList());
                dispatch(staffRequestAction?.resetMessageToInmateSearchList());
                dispatch(staffRequestAction?.resetPrivateConversationSearchList());
                navigate("/Admin/StaffRequestList");
                break;
            case "Connection Requests":
            case "Connection Request":
                dispatch(connectionRequestActions?.resetConnectionReqSearchData());
                navigate("/Admin/ConnectionRequestList");
                break;
            case "Quarantine":
                dispatch(messagesAction.resetMessageSearchData());
                navigate("/Admin/MessageList");
                break;
            default:
                break;
        }
    };

    // ------------------------------
    // Custom Tooltip
    // ------------------------------
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip bg-dark text-white p-2 rounded shadow">
                    <p className="m-0">
                        Facility: <strong>{label}</strong>
                    </p>
                    <p className="m-0">
                        Count:{" "}
                        <strong className="NotificationCount">
                            {payload[0].value}
                        </strong>
                    </p>
                </div>
            );
        }
        return null;
    };

    // ------------------------------
    // Render Custom Tick
    // ------------------------------
    const renderCustomTick = ({ x, y, payload }) => (
        <text
            x={x}
            y={y + 10}
            textAnchor="end"
            transform={`rotate(-45, ${x}, ${y + 10})`}
            className="custom-tick"
        >
            {payload.value}
        </text>
    );

    // ------------------------------
    // Render UI
    // ------------------------------

    if (isLoading) return <CustomLoader />;

    return (
        <div className="admin-dashboard container-fluid">
            <div className="row flex-wrap">
                {/* Sidebar */}
                <div className="sidebar col-lg-2 col-md-3 col-12 mb-3">
                    {sidebarMenu?.map((item) => (
                        <div
                            key={item.type}
                            onClick={() => setActiveTab(item.type)}
                            className={`sidebar-item d-flex align-items-center p-2 ${activeTab === item.type ? "active" : ""
                                }`}
                        >
                            <div className="card-icon me-3">
                                <i className={item.icon}></i>
                            </div>
                            <div className="flex-grow-1">
                                <div>{item.title}</div>
                                <div className="NotificationCount fw-bold">
                                    {item.NotificationCount}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Main Content */}
                <div className="main-content col-lg-10 col-md-9 col-12">
                    <div className="main-title mb-3">
                        <h2>{activeTab} Report</h2>
                    </div>

                    <div className="chart-wrapper">
                        <div
                            className="chart-inner"
                            style={{
                                width:
                                    chartData.length >= 10
                                        ? `${chartData.length *
                                        (chartData.length <= 15
                                            ? 73
                                            : chartData.length <= 20
                                                ? 60
                                                : chartData.length <= 40
                                                    ? 50
                                                    : 35)
                                        }px`
                                        : "100%", // auto width for < 10
                            }}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData}
                                    onClick={(data) => {
                                        if (data) {
                                            if (data?.activeLabel && data?.activeTooltipIndex != "0") {
                                                const clickedData = chartData.find(item => item.FacilityCode === data.activeLabel);
                                                handleFacilityClick(clickedData);
                                            }

                                        }
                                    }}>
                                    <defs>
                                        <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                                            {/* Start with light color at the top */}
                                            <stop offset="0%" stopColor="#2d353c" />
                                            {/* End with dark color at the bottom */}
                                            <stop offset="100%" stopColor="rgba(45, 53, 60, 0.5)" />
                                        </linearGradient>
                                    </defs>

                                    <CartesianGrid strokeDasharray="3 3" />

                                    <XAxis
                                        dataKey="FacilityCode"
                                        angle={-45}
                                        textAnchor="end"
                                        interval={0}
                                        height={100}
                                        tick={renderCustomTick}
                                        label={{ value: `Facility (${chartData?.length})`, position: 'insideBottom', offset: 0, style: { fill: '#34495e', fontWeight: 'bold' } }}
                                    />

                                    <YAxis
                                        label={{ value: `Count  ( ${chartData[chartData?.length - 1]?.MessageCount} - ${chartData[0]?.MessageCount} )`, angle: -90, position: 'insideLeft', offset: 10, style: { fill: '#34495e', fontWeight: 'bold' } }}
                                    />

                                    <Tooltip content={<CustomTooltip />} />

                                    <Bar
                                        dataKey="MessageCount"
                                        onClick={handleFacilityClick}
                                        fill="url(#barGradient)"
                                        radius={[10, 10, 0, 0]}
                                        minPointSize={5}
                                        barSize={40}
                                    />
                                </BarChart>
                            </ResponsiveContainer>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default React.memo(AdminDashboard);
